import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut, FileText, Mail, MessageSquare, Loader2, RefreshCw, ShieldCheck, HeartHandshake } from "lucide-react";
import { usePageMeta } from "@/components/kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const inputCls =
  "w-full bg-[#0A0A0C] border border-gold/20 rounded-sm px-4 py-3 text-sm text-[#F4EFE6] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/30 transition-colors";

export default function AdminPage() {
  usePageMeta("Owner Admin | Redeemer Solutions LLC", "Owner-only dashboard for Redeemer Solutions LLC.");
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [tab, setTab] = useState("quotes");
  const [data, setData] = useState({ quotes: [], contacts: [], chats: [], prayers: [] });
  const [loadingData, setLoadingData] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error("not authenticated");
        setUser(await res.json());
      } catch {
        setUser(null);
      } finally {
        setChecking(false);
      }
    })();
  }, [user]);

  const load = async () => {
    setLoadingData(true);
    try {
      const [subs, chats] = await Promise.all([
        fetch(`${API}/admin/submissions`, { credentials: "include" }),
        fetch(`${API}/admin/chats`, { credentials: "include" }),
      ]);
      if (!subs.ok || !chats.ok) throw new Error("forbidden");
      const s = await subs.json();
      const c = await chats.json();
      setData({ quotes: s.quotes || [], contacts: s.contacts || [], chats: c.chats || [], prayers: s.prayers || [] });
    } catch {
      /* stay on current data */
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (user?.is_owner) load();
  }, [user]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const emailLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(formatApiErrorDetail(d.detail));
      }
      setUser(await res.json());
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  if (checking) {
    return (
      <div data-testid="admin-loading" className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="text-gold animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login" className="min-h-[70vh] flex items-center justify-center px-5 py-16">
        <div className="bg-[#121216] border border-gold/20 rounded-sm p-10 sm:p-12 text-center max-w-md w-full">
          <img src="/logo.png" alt="Redeemer Solutions" className="h-14 w-auto mx-auto" />
          <h1 className="font-display text-2xl font-bold text-[#F4EFE6] mt-7">Owner Access</h1>
          <p className="text-zinc-400 text-sm leading-relaxed mt-3">
            Sign in to view quote requests, messages, and chat transcripts.
          </p>
          <button
            data-testid="admin-google-signin-button"
            onClick={signIn}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold text-sm tracking-wide px-7 py-4 rounded-sm transition-transform duration-300 hover:scale-[1.02]"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#0A0A0C" d="M21.35 11.1H12v3.9h5.35c-.5 2.4-2.55 3.9-5.35 3.9a6 6 0 1 1 0-12c1.5 0 2.9.55 3.95 1.45l2.85-2.85A10 10 0 1 0 12 22c5.8 0 9.65-4.05 9.65-9.75 0-.45-.05-.8-.3-1.15Z"/>
            </svg>
            Sign in with Google
          </button>
          <div className="flex items-center gap-4 my-7">
            <div className="gold-hairline flex-1" />
            <span className="font-mono-label text-[10px] uppercase tracking-[0.22em] text-zinc-500">or with email</span>
            <div className="gold-hairline flex-1" />
          </div>
          <form data-testid="admin-email-login-form" onSubmit={emailLogin} className="space-y-4 text-left">
            <div>
              <label htmlFor="admin-email" className="block font-mono-label text-[10px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Email</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                autoComplete="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className={inputCls}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block font-mono-label text-[10px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Password</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••••••"
              />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="text-red-400 text-sm text-center">{loginError}</p>
            )}
            <button
              type="submit"
              data-testid="admin-email-login-button"
              disabled={loginBusy}
              className="w-full inline-flex items-center justify-center gap-2 border border-gold/50 text-gold-champagne font-semibold text-sm tracking-wide px-7 py-4 rounded-sm transition-colors duration-300 hover:bg-gold/10 disabled:opacity-60"
            >
              {loginBusy && <Loader2 size={15} className="animate-spin" />}
              {loginBusy ? "Signing in..." : "Sign in with Email"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!user.is_owner) {
    return (
      <div data-testid="admin-restricted" className="min-h-[70vh] flex items-center justify-center px-5">
        <div className="bg-[#121216] border border-gold/20 rounded-sm p-10 text-center max-w-md w-full">
          <ShieldCheck size={30} className="text-gold mx-auto" />
          <h1 className="font-display text-2xl font-bold text-[#F4EFE6] mt-5">Restricted Area</h1>
          <p className="text-zinc-400 text-sm mt-3">
            Signed in as {user.email}. This dashboard is reserved for the owner of Redeemer Solutions LLC.
          </p>
          <button data-testid="admin-logout-button" onClick={logout} className="mt-7 text-gold text-sm hover:text-gold-champagne transition-colors inline-flex items-center gap-2">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "quotes", label: `Quotes (${data.quotes.length})`, icon: FileText, testid: "admin-tab-quotes" },
    { id: "messages", label: `Messages (${data.contacts.length})`, icon: Mail, testid: "admin-tab-messages" },
    { id: "prayers", label: `Prayers (${data.prayers.length})`, icon: HeartHandshake, testid: "admin-tab-prayers" },
    { id: "chats", label: `Chats (${data.chats.length})`, icon: MessageSquare, testid: "admin-tab-chats" },
  ];

  return (
    <div data-testid="admin-dashboard" className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-gold">Owner Dashboard</p>
          <h1 className="font-display text-3xl font-bold text-[#F4EFE6] mt-2">Welcome, {user.name || "Zach"}</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-gold/40 text-gold-champagne text-sm px-4 py-2.5 rounded-sm hover:bg-gold/10 transition-colors"
          >
            <RefreshCw size={14} className={loadingData ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 border border-gold/40 text-gold-champagne text-sm px-4 py-2.5 rounded-sm hover:bg-gold/10 transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="flex gap-2 mt-10 border-b border-gold/15 pb-0 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={t.testid}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.id ? "border-gold text-gold" : "border-transparent text-zinc-400 hover:text-gold-champagne"
            }`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {tab === "quotes" &&
          (data.quotes.length === 0 ? (
            <p className="text-zinc-500 text-sm py-10 text-center" data-testid="admin-quotes-empty">No quote requests yet.</p>
          ) : (
            data.quotes.map((q, i) => (
              <div key={q.id || i} data-testid={`admin-quote-${i}`} className="bg-[#121216] border border-gold/15 rounded-sm p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-base font-semibold text-[#F4EFE6]">{q.name} <span className="text-gold text-sm font-normal">· {q.service}</span></p>
                  <p className="text-xs text-zinc-500">{fmt(q.created_at)}</p>
                </div>
                <p className="text-sm text-zinc-400 mt-2">
                  <a href={`tel:${q.phone}`} className="text-gold-champagne">{q.phone}</a> · {q.email}
                  {q.property_type ? ` · ${q.property_type}` : ""}
                </p>
                {q.message && <p className="text-sm text-zinc-300 mt-3 leading-relaxed border-l-2 border-gold/30 pl-4">{q.message}</p>}
              </div>
            ))
          ))}
        {tab === "messages" &&
          (data.contacts.length === 0 ? (
            <p className="text-zinc-500 text-sm py-10 text-center" data-testid="admin-messages-empty">No messages yet.</p>
          ) : (
            data.contacts.map((c, i) => (
              <div key={c.id || i} data-testid={`admin-message-${i}`} className="bg-[#121216] border border-gold/15 rounded-sm p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-base font-semibold text-[#F4EFE6]">{c.name}</p>
                  <p className="text-xs text-zinc-500">{fmt(c.created_at)}</p>
                </div>
                <p className="text-sm text-zinc-400 mt-2">
                  {c.email}{c.phone ? <> · <a href={`tel:${c.phone}`} className="text-gold-champagne">{c.phone}</a></> : ""}
                </p>
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed border-l-2 border-gold/30 pl-4">{c.message}</p>
              </div>
            ))
          ))}
        {tab === "prayers" &&
          (data.prayers.length === 0 ? (
            <p className="text-zinc-500 text-sm py-10 text-center" data-testid="admin-prayers-empty">No prayer requests yet.</p>
          ) : (
            data.prayers.map((p, i) => (
              <div key={p.id || i} data-testid={`admin-prayer-${i}`} className="bg-[#121216] border border-gold/15 rounded-sm p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-display text-base font-semibold text-[#F4EFE6]">{p.name || "Anonymous"}</p>
                  <p className="text-xs text-zinc-500">{fmt(p.created_at)}</p>
                </div>
                <p className="text-sm text-zinc-400 mt-2">{[p.email, p.phone].filter(Boolean).join(" · ") || "No contact info shared"}</p>
                <p className="text-sm text-zinc-300 mt-3 leading-relaxed border-l-2 border-gold/30 pl-4">{p.request}</p>
              </div>
            ))
          ))}
        {tab === "chats" &&
          (data.chats.length === 0 ? (
            <p className="text-zinc-500 text-sm py-10 text-center" data-testid="admin-chats-empty">No chat transcripts yet.</p>
          ) : (
            data.chats.map((c, i) => (
              <div key={i} data-testid={`admin-chat-${i}`} className="bg-[#121216] border border-gold/15 rounded-sm p-6">
                <p className="text-xs text-zinc-500">{fmt(c.created_at)} · session {String(c.session_id).slice(0, 8)}</p>
                <p className="text-sm text-zinc-300 mt-3"><span className="text-gold font-mono-label text-[10px] uppercase tracking-[0.18em]">Visitor</span><br />{c.user}</p>
                <p className="text-sm text-zinc-300 mt-3"><span className="text-gold font-mono-label text-[10px] uppercase tracking-[0.18em]">Assistant</span><br />{c.assistant}</p>
              </div>
            ))
          ))}
      </div>
    </div>
  );
}
